import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Sparkles, Upload, Share2, Trophy, Clock } from 'lucide-react';
import { useOnboarding } from '../onboarding/OnboardingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_LABEL = {
    '<1h': '< 1 hr/day',
    '1-2h': '1–2 hrs/day',
    '2-4h': '2–4 hrs/day',
    '4h+': '4+ hrs/day',
};

export default function AITutor() {
    const { user } = useOnboarding();
    const identity = (user?.email || user?.name || 'guest@studyfolio.io').toLowerCase();
    const goal = user?.goal || 'CBSE Class 10 Board preparation';
    const boardClass = user?.board_class || 'CBSE Class 10';
    const focusAreas = user?.focus_areas || [];
    const primaryObjective = user?.primary_objective || '';
    const timeCommitment = user?.time_commitment || '';

    const [minutes, setMinutes] = useState(user?.daily_minutes || 30);
    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(false);
    const [answers, setAnswers] = useState({});
    const [scored, setScored] = useState(false);
    const [gam, setGam] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [share, setShare] = useState(false);
    const fileRef = useRef(null);

    const loadGam = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/gamification/me`, { params: { identity } });
            setGam(r.data);
        } catch (e) { console.error(e); }
    }, [identity]);

    useEffect(() => { loadGam(); }, [loadGam]);

    async function award(reason, amount) {
        try {
            const r = await axios.post(`${API}/gamification/award`, { identity, reason, amount });
            setGam((g) => ({ ...(g || {}), xp: r.data.xp, badge: r.data.badge, next: r.data.next }));
        } catch (e) { console.error(e); }
    }

    async function genQuiz() {
        setLoading(true); setQuiz(null); setScored(false); setAnswers({});
        try {
            const r = await axios.post(`${API}/tutor/quiz`, {
                goal,
                minutes,
                level: user?.education || 'beginner',
                board_class: boardClass,
                focus_areas: focusAreas,
                primary_objective: primaryObjective,
                time_commitment: timeCommitment,
            });
            setQuiz(r.data);
        } catch (e) {
            console.error(e);
            alert('Metis is thinking — please try again in a moment.');
        } finally { setLoading(false); }
    }

    function submitQuiz() {
        if (!quiz) return;
        let correct = 0;
        quiz.questions.forEach((q, i) => { if (answers[i] === q.answer) correct += 1; });
        setScored(true);
        const pct = correct / quiz.questions.length;
        award('daily-quiz', Math.round(50 + pct * 100)); // 50-150 XP
    }

    async function onFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setAnalyzing(true); setAnalysis(null);
        const reader = new FileReader();
        reader.onload = async () => {
            const b64 = String(reader.result).split(',')[1];
            try {
                const r = await axios.post(`${API}/tutor/analyze`, { image_base64: b64, mime: file.type, goal });
                setAnalysis(r.data.feedback);
                award('notes-upload', 40);
            } catch (err) {
                console.error(err);
                setAnalysis('Could not read that image — try a clearer photo of your notes.');
            } finally { setAnalyzing(false); }
        };
        reader.readAsDataURL(file);
    }

    function shareAchievement(platform) {
        const badge = gam?.badge?.name || 'Focus Novice';
        const text = `I just reached "${badge}" (${gam?.xp || 0} XP) on Studyfolio while prepping — ${goal}! 📚✨`;
        const url = window.location.origin;
        const map = {
            twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
            reddit: `https://www.reddit.com/submit?title=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
            whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
        };
        if (platform === 'native' && navigator.share) {
            navigator.share({ title: 'Studyfolio', text, url }).catch(() => {});
            return;
        }
        window.open(map[platform] || map.twitter, '_blank', 'noopener');
    }

    const xp = gam?.xp || 0;
    const badge = gam?.badge || { name: 'Focus Novice', color: '#A8C69A' };
    const next = gam?.next;
    const progress = next ? Math.min(100, Math.round((xp / next.threshold) * 100)) : 100;

    return (
        <div className="space-y-6 mb-10" data-testid="ai-tutor">
            {/* Metis intro banner */}
            <div className="border border-[color:var(--moss-deep)]/30 rounded-sm bg-gradient-to-br from-white/60 to-[color:var(--lavender)]/30 p-4" data-testid="metis-banner">
                <div className="flex items-center gap-2 mono text-[10px] text-[color:var(--lavender-deep)]">
                    <Sparkles size={12} /> METIS · YOUR AI STUDY ADVISOR &amp; MENTOR
                </div>
                <div className="display text-[color:var(--ink)] text-xl mt-1" data-testid="metis-title">
                    Hello{user?.name ? `, ${user.name.split(' ')[0]}` : ''} — I'm Metis.
                </div>
                <div className="text-[13px] text-[color:var(--ink)]/80 mt-1">
                    A Socratic mentor for your {boardClass} journey. I'll craft questions from the NCERT syllabus,
                    tuned to your {timeCommitment ? `${TIME_LABEL[timeCommitment] || timeCommitment} ` : ''}study
                    rhythm{focusAreas.length ? ` across ${focusAreas.slice(0, 3).join(', ')}${focusAreas.length > 3 ? '…' : ''}` : ''}.
                </div>
            </div>

            {/* XP / Badge header */}
            <div className="flex items-center justify-between gap-4 border border-[color:var(--moss-deep)]/30 rounded-sm bg-white/40 p-4 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full flex items-center justify-center" style={{ background: badge.color, boxShadow: `0 0 18px ${badge.color}` }}>
                        <Trophy size={18} className="text-[color:var(--moss-dark)]" />
                    </div>
                    <div>
                        <div className="display text-[color:var(--ink)] text-lg leading-none" data-testid="badge-name">{badge.name}</div>
                        <div className="mono text-[10px] text-[color:var(--moss-deep)] mt-1" data-testid="xp-value">{xp} XP{next ? ` · ${next.threshold - xp} to ${next.name}` : ' · max level'}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setShare(!share)} data-testid="share-achievement"
                        className="btn-ghost-moss px-3 py-2 rounded-sm text-[11px] mono flex items-center gap-2">
                        <Share2 size={12} /> Share
                    </button>
                </div>
                <div className="w-full h-[3px] bg-[color:var(--moss-deep)]/15 rounded-full overflow-hidden">
                    <motion.div className="h-full" style={{ background: badge.color }} animate={{ width: `${progress}%` }} transition={{ duration: 0.8 }} />
                </div>
                <AnimatePresence>
                    {share && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                            className="w-full flex gap-2 pt-1" data-testid="share-options">
                            {['twitter', 'reddit', 'whatsapp', 'native'].map((p) => (
                                <button key={p} onClick={() => shareAchievement(p)}
                                    className="btn-lavender px-3 py-1.5 rounded-sm text-[10px] mono capitalize" data-testid={`share-${p}`}>
                                    {p === 'native' ? 'More…' : p}
                                </button>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Daily goal + generate */}
            <div className="border border-[color:var(--moss-deep)]/30 rounded-sm bg-white/40 p-4 space-y-3">
                <div className="flex items-center gap-2 mono text-[10px] text-[color:var(--moss-deep)]">
                    <Sparkles size={12} /> METIS · CBSE-tuned quiz engine
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <Clock size={14} className="text-[color:var(--moss-deep)]" />
                    <span className="text-sm text-[color:var(--ink)]">How many minutes can you study today?</span>
                    <div className="flex gap-1.5">
                        {[15, 30, 45, 60].map((m) => (
                            <button key={m} onClick={() => setMinutes(m)} data-testid={`minutes-${m}`}
                                className={`px-3 py-1 rounded-full mono text-[11px] border ${minutes === m ? 'bg-[color:var(--lavender)] border-[color:var(--lavender-deep)] text-[color:var(--moss-dark)]' : 'border-[color:var(--moss-deep)]/40 text-[color:var(--moss-deep)]'}`}>
                                {m}m
                            </button>
                        ))}
                    </div>
                </div>
                <button onClick={genQuiz} disabled={loading} data-testid="generate-quiz"
                    className="btn-lavender px-5 py-2.5 rounded-sm text-xs mono disabled:opacity-50">
                    {loading ? 'Metis is crafting your quiz…' : 'Ask Metis for today\'s quiz →'}
                </button>
            </div>

            {/* AI quiz */}
            <AnimatePresence>
                {quiz && (
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="ai-quiz">
                        <div className="mono text-[11px] text-[color:var(--moss-deep)]">TOPIC · {quiz.topic}</div>
                        {quiz.questions.map((q, i) => (
                            <div key={i} className="border-b border-[color:var(--moss-deep)]/20 pb-3">
                                <div className="text-[15px] text-[color:var(--ink)] mb-2">{i + 1}. {q.q}</div>
                                <div className="grid md:grid-cols-2 gap-2">
                                    {q.opts.map((o, oi) => {
                                        const chosen = answers[i] === oi;
                                        const showCorrect = scored && oi === q.answer;
                                        const showWrong = scored && chosen && oi !== q.answer;
                                        return (
                                            <button key={oi} disabled={scored}
                                                onClick={() => setAnswers((a) => ({ ...a, [i]: oi }))}
                                                data-testid={`aiq-${i}-opt-${oi}`}
                                                className={`text-left px-3 py-2 rounded-sm border text-[13px] transition-all ${showCorrect ? 'bg-green-100 border-green-500' : showWrong ? 'bg-red-100 border-red-400' : chosen ? 'bg-[color:var(--lavender)]/40 border-[color:var(--lavender-deep)]' : 'border-[color:var(--moss-deep)]/30 bg-white/40'}`}>
                                                <span className="mono text-[9px] text-[color:var(--moss-deep)] mr-2">{String.fromCharCode(65 + oi)}</span>
                                                {o}
                                            </button>
                                        );
                                    })}
                                </div>
                                {scored && <div className="mono text-[10px] text-[color:var(--moss-deep)] mt-2">✎ {q.explain}</div>}
                            </div>
                        ))}
                        {!scored ? (
                            <button onClick={submitQuiz} data-testid="submit-ai-quiz" className="btn-lavender px-5 py-2.5 rounded-sm text-xs mono">
                                Submit &amp; earn XP
                            </button>
                        ) : (
                            <div className="mono text-[11px] text-[color:var(--lavender-deep)]">
                                ✦ {quiz.questions.filter((q, i) => answers[i] === q.answer).length}/{quiz.questions.length} correct — XP awarded!
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Notes upload */}
            <div className="border border-dashed border-[color:var(--moss-deep)]/40 rounded-sm p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="mono text-[10px] text-[color:var(--moss-deep)]">Upload your notes / test paper → Metis gives feedback (+40 XP)</div>
                    <button onClick={() => fileRef.current?.click()} disabled={analyzing} data-testid="upload-notes"
                        className="btn-ghost-moss px-4 py-2 rounded-sm text-[11px] mono flex items-center gap-2">
                        <Upload size={12} /> {analyzing ? 'Reading…' : 'Upload image'}
                    </button>
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="notes-input" />
                </div>
                {analysis && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 text-[13px] text-[color:var(--ink)] display-italic border-l-2 border-[color:var(--lavender-deep)] pl-3" data-testid="analysis-result">
                        {analysis}
                    </motion.div>
                )}
            </div>
        </div>
    );
}
